import { PartRelationshipNode, PartRelationshipNodeType, ThumbnailMatchType } from '@/api/generatedApi';
import { RelatedBadge, thumbnailCaption } from '@/components/PartFinder/PartFinderThumbnailCell';
import { PartFinderThumbnailHover, ThumbnailFrame } from '@/components/PartFinder/PartFinderThumbnailHover';
import { useGetPartFinderReportThumbnail } from '@/hooks/api/part-finder-reports/useGetPartFinderReportThumbnail';
import {
  FactoryOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  StorefrontOutlined,
  TagOutlined,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { Handle, Node, NodeProps, Position } from '@xyflow/react';
import { ReactNode } from 'react';

export type RelationshipFlowNode = Node<
  { node: PartRelationshipNode; isSeed: boolean; showThumbnail: boolean; relation?: string },
  'relationship'
>;

const NODE_TYPE_CAPTIONS: Record<PartRelationshipNodeType, string> = {
  [PartRelationshipNodeType.Nsn]: 'NSN',
  [PartRelationshipNodeType.PartNumber]: 'Part number',
  [PartRelationshipNodeType.Cage]: 'CAGE',
  [PartRelationshipNodeType.EndItem]: 'End item',
  [PartRelationshipNodeType.Supplier]: 'Supplier',
};

const NODE_TYPE_ICONS: Record<PartRelationshipNodeType, ReactNode> = {
  [PartRelationshipNodeType.Nsn]: <Inventory2Outlined fontSize='small' />,
  [PartRelationshipNodeType.PartNumber]: <TagOutlined fontSize='small' />,
  [PartRelationshipNodeType.Cage]: <FactoryOutlined fontSize='small' />,
  [PartRelationshipNodeType.EndItem]: <LocalShippingOutlined fontSize='small' />,
  [PartRelationshipNodeType.Supplier]: <StorefrontOutlined fontSize='small' />,
};

export const nodeTypeColor = (theme: Theme, type: PartRelationshipNodeType): string => {
  const colors: Record<PartRelationshipNodeType, string> = {
    [PartRelationshipNodeType.Nsn]: theme.palette.primary.main,
    [PartRelationshipNodeType.PartNumber]: theme.palette.info.main,
    [PartRelationshipNodeType.Cage]: theme.palette.secondary.main,
    [PartRelationshipNodeType.EndItem]: theme.palette.warning.main,
    [PartRelationshipNodeType.Supplier]: theme.palette.success.main,
  };
  return colors[type];
};

export const nodeTypeCaption = (type: PartRelationshipNodeType): string => NODE_TYPE_CAPTIONS[type];

const HIDDEN_HANDLE = { opacity: 0, pointerEvents: 'none' } as const;
const NodeVisual = ({ node, showThumbnail }: { node: PartRelationshipNode; showThumbnail: boolean }) => {
  const theme = useTheme();
  const { thumbnail } = useGetPartFinderReportThumbnail({
    reportId: node.reportId ?? '',
    enabled: showThumbnail,
  });
  if (!thumbnail) {
    return (
      <ThumbnailFrame sx={{ color: nodeTypeColor(theme, node.type) }}>{NODE_TYPE_ICONS[node.type]}</ThumbnailFrame>
    );
  }
  const alt = `${node.label} thumbnail`;
  return (
    <PartFinderThumbnailHover thumbnailUrl={thumbnail} alt={alt} caption={thumbnailCaption(node)}>
      <ThumbnailFrame>
        <img src={thumbnail} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {node.thumbnailMatchType === ThumbnailMatchType.Related && <RelatedBadge>~</RelatedBadge>}
      </ThumbnailFrame>
    </PartFinderThumbnailHover>
  );
};

export const RelationshipNode = ({ data }: NodeProps<RelationshipFlowNode>) => {
  const theme = useTheme();
  const { node, isSeed, showThumbnail, relation } = data;
  return (
    <Box
      data-testid={`relationship-node-${node.id}`}
      sx={{
        width: 240,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 8px',
        borderRadius: 1,
        border: `${isSeed ? 2 : 1}px solid ${isSeed ? theme.palette.primary.main : theme.palette.divider}`,
        borderLeft: `4px solid ${nodeTypeColor(theme, node.type)}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
      }}
    >
      <Handle id='left-target' type='target' position={Position.Left} style={HIDDEN_HANDLE} isConnectable={false} />
      <Handle id='left-source' type='source' position={Position.Left} style={HIDDEN_HANDLE} isConnectable={false} />
      <NodeVisual node={node} showThumbnail={showThumbnail} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='caption' color='text.secondary' component='div' noWrap>
          {isSeed ? 'This part' : (relation ?? nodeTypeCaption(node.type))}
        </Typography>
        <Typography variant='body2' noWrap sx={{ fontWeight: isSeed ? 600 : 400 }}>
          {node.label}
        </Typography>
        {node.subtitle && (
          <Typography variant='caption' color='text.secondary' component='div' noWrap>
            {node.subtitle}
          </Typography>
        )}
      </Box>
      <Handle id='right-target' type='target' position={Position.Right} style={HIDDEN_HANDLE} isConnectable={false} />
      <Handle id='right-source' type='source' position={Position.Right} style={HIDDEN_HANDLE} isConnectable={false} />
    </Box>
  );
};
